// @ts-nocheck
/**
 * Demo Account Settings list — redesigned to obd3-gui pattern (FillPage +
 * PageHeader + per-row Card with 10-tile grid + ConfirmDialog).
 * SUPERADMIN-only.
 * v1 quirk preserved: Add Setting button hidden when list.length === 1.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Pencil,
  Plus,
  Trash2,
  Wallet,
  Phone,
  Users,
  UserCheck,
  UsersRound,
  Mic,
  Building2,
  Crown,
  CalendarClock,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import Loader from '@/components/partials/components/Loader';
import { getApiCall, deleteApiCall } from '@/service/httpCall';
import {
  API_DEMO_ACCOUNT_SETTINGS_LIST,
  API_DEMO_ACCOUNT_SETTINGS_DELETE,
} from '@/service/url';
import { ROUTES, USER_ROLES } from '@/utilities/constant';
import { getUser } from '@/utilities/setting';

const COLUMNS = [
  { key: 'credit_limit_each_time', label: 'Credit', icon: Wallet, tone: 'emerald' },
  { key: 'did_per_demo_company', label: 'DID Per Company', icon: Phone, tone: 'sky' },
  { key: 'total_demo_agent_per_company', label: 'Total Agents / Company', icon: Users, tone: 'primary' },
  { key: 'total_demo_live_agent_per_company', label: 'Live Agents / Company', icon: UserCheck, tone: 'primary' },
  { key: 'total_demo_live_agent_overall', label: 'Live Agents Overall (End User)', icon: UsersRound, tone: 'violet' },
  { key: 'tts_per_demo_company_per_day', label: 'TTS / Company / Day', icon: Mic, tone: 'orange' },
  { key: 'call_center_per_company', label: 'Call Centers / Company', icon: Building2, tone: 'sky' },
  { key: 'team_lead_per_company', label: 'Team Leads / Company', icon: Crown, tone: 'amber' },
  { key: 'expiry_days_per_recharge', label: 'Expiry Days / Recharge', icon: CalendarClock, tone: 'rose' },
  { key: 'number_of_times_demo_account_can_be_recharged', label: 'Recharge Count', icon: RefreshCw, tone: 'teal' },
];

const TONE_MAP: Record<string, string> = {
  emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
  sky: 'bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300',
  primary: 'bg-primary/10 text-primary',
  violet: 'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300',
  orange: 'bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
  rose: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300',
  teal: 'bg-teal-100 text-teal-700 dark:bg-teal-950/40 dark:text-teal-300',
};

export default function DemoAccountSettingsList() {
  const navigate = useNavigate();
  const [list, setList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const user = getUser();
    if (!user || user.role !== USER_ROLES.SUPERADMIN) {
      toast.error('Only Super Admin can access demo account settings');
      navigate('/');
      return;
    }
    loadList();
  }, []);

  function loadList() {
    setLoading(true);
    getApiCall(API_DEMO_ACCOUNT_SETTINGS_LIST)
      .then((resp: any) => setList(Array.isArray(resp) ? resp : []))
      .catch((err: any) => toast.error(String(err)))
      .finally(() => setLoading(false));
  }

  const confirmDelete = () => {
    if (!pendingDelete) return;
    setDeleting(true);
    deleteApiCall(API_DEMO_ACCOUNT_SETTINGS_DELETE.replace(':id', pendingDelete.id))
      .then(() => {
        toast.success('Deleted successfully');
        setPendingDelete(null);
        loadList();
      })
      .catch((err: any) => toast.error(String(err)))
      .finally(() => setDeleting(false));
  };

  if (loading && list.length === 0) {
    return (
      <FillPage>
        <Loader />
      </FillPage>
    );
  }

  return (
    <FillPage>
      <PageHeader
        title="Demo Account Settings"
        description="Global limits applied to demo companies — credits, DIDs, agents, TTS, expiry."
        actions={
          list.length !== 1 && (
            <Button onClick={() => navigate(`/${ROUTES.DEMO_ACCOUNT_SETTINGS.CREATE}`)}>
              <Plus className="h-4 w-4" />
              Add Setting
            </Button>
          )
        }
      />

      {list.length === 0 ? (
        <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          No demo account settings found.
        </div>
      ) : (
        <div className="space-y-4">
          {list.map((row: any, idx: number) => (
            <Card key={row.id} className="border-border/60">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                <CardTitle className="text-base">
                  {list.length > 1 ? `Setting #${idx + 1}` : 'Current Settings'}
                </CardTitle>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    title="Edit"
                    onClick={() =>
                      navigate(
                        `/${ROUTES.DEMO_ACCOUNT_SETTINGS.EDIT.replace(':id', row.id)}`,
                      )
                    }
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary transition-colors hover:bg-primary/15"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  { /* <button
                    type="button"
                    title="Delete"
                    onClick={() => setPendingDelete(row)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-destructive/10 text-destructive transition-colors hover:bg-destructive/15"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                  */ }
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
                  {COLUMNS.map((c: any) => {
                    const Icon = c.icon;
                    return (
                      <div
                        key={c.key}
                        className="flex items-center gap-3 rounded-lg border border-border/50 bg-card p-3 shadow-neu-raised"
                      >
                        <span
                          className={
                            'flex h-10 w-10 shrink-0 items-center justify-center rounded-md ' +
                            (TONE_MAP[c.tone] || TONE_MAP.primary)
                          }
                        >
                          <Icon className="h-5 w-5" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                            {c.label}
                          </p>
                          <p className="text-lg font-bold text-foreground tabular-nums">
                            {row[c.key] ?? '—'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete this demo account setting?"
        description="These global limits will be removed. This action cannot be undone."
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={confirmDelete}
      />
    </FillPage>
  );
}
